import { AppMasterDBFacade, InputAppClientDBFacade } from '@monorepo/core-data';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, take, switchMap } from 'rxjs';
import { AssemblyInDataProvider } from './assembly-in.data.provider';
import {
  BctxViz,
  TAssemblyInBundleTransactionDetailsResponse,
  TAssemblyInBundleTransactionResponse,
  TAssemblyInComponentGroupUndoResponseDTO,
  TAssemblyInComponentRequestDTO,
  TAssemblyInComponentsGroupAddResponseDTO,
  TAssemblyInTransactionDetailsResponse,
  TAssemblyInTransactionResponse,
  TComponentTagProcessHistoryRequestDTO,
  TComponentTagsWipInfoByBundleTagResponseDTO,
  TComponentTagWipInfoResponseDTO,
  TTAssemblyInBundleTransactionRecord,
  TUndoBundleTagResponse,
  TUserPaginationOption,
} from '@monorepo/types';

@Injectable({
  providedIn: 'root',
})
export class AssemblyInCommonService {
  constructor(
    private appMasterDBFacade: AppMasterDBFacade,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private assemblyInDataProvider: AssemblyInDataProvider,
  ) {}

  fetchComponentTagWipInfo(componentTagDisplayId: string): Observable<TComponentTagWipInfoResponseDTO> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.assemblyInDataProvider.fetchComponentTagWipInfo({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          componentDisplayTagId: componentTagDisplayId,
        });
      }),
    );
  }

  getComponentTagProcessHistory(componentTagDisplayId: string) {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getBctxViz().pipe(
          take(1),
          map((buzKeys) => [buzKeys, subjectKey] as const),
        );
      }),
      switchMap(([buzKeys, subjectKey]) => {
        return this.fetchComponentTagWipInfo(componentTagDisplayId).pipe(
          take(1),
          map((response) => [response, subjectKey, buzKeys] as const),
        );
      }),
      switchMap(([response, subjectKey, buzKeys]) => {
        if (response.status === 'SUCCESS') {
          const tagDetailsMap = response.tagDetails.reduce(
            (acc: Record<string, string | number>, item: TComponentTagWipInfoResponseDTO['tagDetails'][number]) => {
              acc[item.k] = item.v;
              return acc;
            },
            {} as Record<string, string | number>,
          );
          const payload: TComponentTagProcessHistoryRequestDTO = {
            subjectKey: subjectKey,
            componentTagId: tagDetailsMap['componentTag'] as string,
            componentType: tagDetailsMap['componentType'] as string,
            bctxItemList: buzKeys
              .map((viz) => ({ key: viz.k, value: (tagDetailsMap[viz.k] || '') as string }))
              .filter((item) => item.value && item.key !== 'size'),
            tagQuantity: Number(tagDetailsMap['bundleQuantity'] || 0),
          };
          return this.assemblyInDataProvider.getComponentTagProcessHistory(payload);
        }

        return of({
          status: 'FAILURE',
          history: [],
          error: response.error,
        });
      }),
      catchError(() => {
        return of({
          status: 'FAILURE',
          history: [],
          error: 'Something went wrong',
        });
      }),
    );
  }

  saveComponentTags(tags: TAssemblyInComponentRequestDTO[]): Observable<TAssemblyInComponentsGroupAddResponseDTO> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getAppDetailsWithAdditionalInfo().pipe(
          take(1),
          map((appDetails) => [appDetails, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([appDetails, subjectKey, selectedSubject]) => {
        return this.assemblyInDataProvider.saveComponentTags({
          appDetails: {
            an: appDetails.an,
            appType: appDetails.appType,
            appVersion: appDetails.appVersion,
            deviceId: appDetails.deviceId,
            dnm: appDetails.dnm,
            dnr: appDetails.dnr,
            dnrk: appDetails.dnrk,
            hsk: selectedSubject?.hsk || '',
            qtyValidation: appDetails.qtyValidation,
            shift: appDetails.shift,
            shiftDn: appDetails.shiftDn,
            snm: appDetails.snm,
            subject: selectedSubject?.subject || '',
            subjectKey: subjectKey,
            urId: appDetails.urId,
            urNm: appDetails.urNm,
            wipType: appDetails.wipType,
          },
          subjectKey,
          componentInfoList: tags,
        });
      }),
    );
  }

  fetchTransactions(
    filters: TAssemblyInTransactionResponse['supportedFilters'],
    userPaginationOption: TUserPaginationOption,
  ): Observable<TAssemblyInTransactionResponse> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getBuzKeys().pipe(
          take(1),
          map((buzKeys) => [buzKeys, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([buzKeys, subjectKey, selectedSubject]) => {
        return this.assemblyInDataProvider.fetchTransactionLog({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          filters,
          filterFields: [
            ...this.getAvailableBctxKeys(buzKeys),
            'component_display_tag_id',
            'component_type',
            'bundle_display_tag_id',
          ],
          pageNo: userPaginationOption.pageNo,
          pageSize: userPaginationOption.pageSize,
        });
      }),
    );
  }

  getTransactionDetails(componentTagId: string): Observable<TAssemblyInTransactionDetailsResponse> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.assemblyInDataProvider.getTransactionDetails({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          componentTagId: componentTagId,
        });
      }),
    );
  }

  fetchBundleTransactionLog(
    filters: TAssemblyInBundleTransactionResponse['supportedFilters'],
    userPaginationOption: TUserPaginationOption,
  ): Observable<TAssemblyInBundleTransactionResponse> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getBuzKeys().pipe(
          take(1),
          map((buzKeys) => [buzKeys, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([buzKeys, subjectKey, selectedSubject]) => {
        return this.assemblyInDataProvider.fetchBundleTransactionLog({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          filters,
          filterFields: [...this.getAvailableBctxKeys(buzKeys)],
          pageNo: userPaginationOption.pageNo,
          pageSize: userPaginationOption.pageSize,
        });
      }),
    );
  }

  fetchBundleTransactionDetails(bundleTagId: string): Observable<TAssemblyInBundleTransactionDetailsResponse> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.assemblyInDataProvider.fetchBundleTransactionDetails({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          bundleTagId,
        });
      }),
    );
  }

  undoComponentTags(tag: TAssemblyInComponentRequestDTO): Observable<TAssemblyInComponentGroupUndoResponseDTO> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getAppDetailsWithAdditionalInfo().pipe(
          take(1),
          map((appDetails) => [appDetails, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([appDetails, subjectKey, selectedSubject]) => {
        return this.assemblyInDataProvider.undoComponentTags({
          appDetails: {
            an: appDetails.an,
            appType: appDetails.appType,
            appVersion: appDetails.appVersion,
            deviceId: appDetails.deviceId,
            dnm: appDetails.dnm,
            dnr: appDetails.dnr,
            dnrk: appDetails.dnrk,
            hsk: selectedSubject?.hsk || '',
            qtyValidation: appDetails.qtyValidation,
            shift: appDetails.shift,
            shiftDn: appDetails.shiftDn,
            snm: appDetails.snm,
            subject: selectedSubject?.subject || '',
            subjectKey: subjectKey,
            urId: appDetails.urId,
            urNm: appDetails.urNm,
            wipType: appDetails.wipType,
          },
          subjectKey: subjectKey,
          componentInfo: tag,
        });
      }),
    );
  }

  getAvailableBctxKeys(bctxkeys: string[]) {
    const bctxKeys = ['buyer', 'style', 'po', 'size', 'product_type', 'product_category', 'order_ref', 'color'];
    return bctxKeys.filter((key) => bctxkeys.includes(key));
  }

  getAvailableBctxViz(bctxViz: BctxViz[]) {
    const bctxKeys = ['buyer', 'style', 'po', 'size', 'product_type', 'product_category', 'order_ref', 'color'];
    return bctxViz.filter((key) => bctxKeys.includes(key.k));
  }

  getDisplayName(bctxViz: BctxViz[], key: string) {
    return bctxViz.find((bctx) => bctx.k === key)?.d || '_';
  }

  getComponentTagsWipInfoByBundleTag(
    bundleTagDisplayId: string,
  ): Observable<TComponentTagsWipInfoByBundleTagResponseDTO> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.assemblyInDataProvider.getComponentTagsWipInfoByBundleTag({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          bundleTagDisplayId,
        });
      }),
    );
  }

  undoBundleTag(bundleTransactionRecord: TTAssemblyInBundleTransactionRecord): Observable<TUndoBundleTagResponse> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getAppDetailsWithAdditionalInfo().pipe(
          take(1),
          map((appDetails) => [appDetails, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([appDetails, subjectKey, selectedSubject]) => {
        return this.assemblyInDataProvider.undoBundleTag({
          appDetails: {
            an: appDetails.an,
            appType: appDetails.appType,
            appVersion: appDetails.appVersion,
            deviceId: appDetails.deviceId,
            dnm: appDetails.dnm,
            dnr: appDetails.dnr,
            dnrk: appDetails.dnrk,
            hsk: selectedSubject?.hsk || '',
            qtyValidation: appDetails.qtyValidation,
            shift: appDetails.shift,
            shiftDn: appDetails.shiftDn,
            snm: appDetails.snm,
            subject: selectedSubject?.subject || '',
            subjectKey: subjectKey,
            urId: appDetails.urId,
            urNm: appDetails.urNm,
            wipType: appDetails.wipType,
          },
          subjectKey: subjectKey,
          bundleCutTagInfo: {
            bundleDisplayTagId: bundleTransactionRecord.bundleDisplayTagId,
            bundleTagId: bundleTransactionRecord.bundleTagId,
            bctx: bundleTransactionRecord.bctx,
            tagFields: bundleTransactionRecord.tagFields,
          },
        });
      }),
    );
  }
}
